import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Sun,
    Lightning,
    Fire,
    Clock,
    CheckCircle,
    Trophy,
    WarningCircle,
    ChartLine,
    ArrowSquareOut,
    PhoneCall,
    ArrowsClockwise,
    CheckSquare,
    Square,
    X,
} from "@phosphor-icons/react";

import QuickActions, { QUICK_ACTIONS } from "@/components/QuickActions";
import InlineNotes from "@/components/InlineNotes";
import SavedSearchesPanel from "@/components/SavedSearchesPanel";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Today() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [includeMedium, setIncludeMedium] = useState(true);
    const [selected, setSelected] = useState(() => new Set());
    const [focusedIndex, setFocusedIndex] = useState(0);
    const { email: operator } = useAuth();
    const rowRefs = useRef({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/today`, {
                params: { include_medium: includeMedium },
            });
            setData(data);
        } catch (e) {
            console.error("today load fail", e);
            toast.error("Failed to load Today dashboard");
        } finally {
            setLoading(false);
        }
    }, [includeMedium]);

    useEffect(() => {
        load();
    }, [load]);

    const updateStatus = useCallback(
        async (providerId, nextStatus) => {
            // optimistic update
            setData((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    fresh_providers: prev.fresh_providers.map((p) =>
                        p.id === providerId
                            ? { ...p, contact_status: nextStatus }
                            : p,
                    ),
                    followups: prev.followups.map((p) =>
                        p.id === providerId
                            ? { ...p, contact_status: nextStatus }
                            : p,
                    ),
                };
            });
            try {
                await axios.patch(`${API}/providers/${providerId}`, {
                    contact_status: nextStatus,
                    operator: operator || undefined,
                });
                toast.success(
                    operator
                        ? `${operator} → ${nextStatus}`
                        : `Marked as ${nextStatus}`,
                );
                // reload summary counters
                load();
            } catch (e) {
                toast.error("Failed to update status");
                load();
            }
        },
        [load, operator],
    );

    const saveNote = useCallback(
        async (providerId, currentStatus, note) => {
            try {
                await axios.patch(`${API}/providers/${providerId}`, {
                    contact_status: currentStatus || "Not Contacted",
                    contact_notes: note,
                    operator: operator || undefined,
                });
                toast.success("Note saved");
                setData((prev) => {
                    if (!prev) return prev;
                    const apply = (arr) =>
                        arr.map((p) =>
                            p.id === providerId ? { ...p, contact_notes: note } : p,
                        );
                    return {
                        ...prev,
                        fresh_providers: apply(prev.fresh_providers),
                        followups: apply(prev.followups),
                    };
                });
            } catch (e) {
                toast.error("Failed to save note");
            }
        },
        [operator],
    );

    // --- Bulk selection / actions ---
    const toggleSelect = useCallback((providerId) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(providerId)) next.delete(providerId);
            else next.add(providerId);
            return next;
        });
    }, []);

    const clearSelection = useCallback(() => setSelected(new Set()), []);

    const selectAllVisible = useCallback(() => {
        setSelected((prev) => {
            const next = new Set(prev);
            const fresh = data?.fresh_providers || [];
            fresh.forEach((p) => next.add(p.id));
            return next;
        });
    }, [data]);

    const bulkUpdate = useCallback(
        async (status) => {
            const ids = Array.from(selected);
            if (ids.length === 0) return;
            try {
                const { data: res } = await axios.post(
                    `${API}/providers/bulk`,
                    {
                        provider_ids: ids,
                        contact_status: status,
                        operator: operator || undefined,
                    },
                );
                toast.success(
                    `${res.updated} marked as ${status}${
                        res.unchanged ? ` (${res.unchanged} already that status)` : ""
                    }`,
                );
                clearSelection();
                load();
            } catch (e) {
                toast.error("Bulk update failed");
            }
        },
        [selected, operator, load, clearSelection],
    );

    const handleClaim = useCallback(
        (provider) => {
            const base = data?.claim_url_base;
            if (base) {
                const url = `${base}${base.includes("?") ? "&" : "?"}provider=${encodeURIComponent(provider.id)}&name=${encodeURIComponent(provider.name || "")}&website=${encodeURIComponent(provider.website || "")}`;
                window.open(url, "_blank");
            }
            updateStatus(provider.id, "Claimed Business");
        },
        [data?.claim_url_base, updateStatus],
    );

    // --- Keyboard shortcuts (1-9 for QuickActions on the focused row) ---
    useEffect(() => {
        const handler = (e) => {
            const target = e.target;
            const isTyping =
                target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable);
            if (isTyping) return;

            if (e.key === "Escape") {
                clearSelection();
                return;
            }

            if (e.key === "ArrowDown" || e.key === "j") {
                setFocusedIndex((idx) =>
                    Math.min((data?.fresh_providers || []).length - 1, idx + 1),
                );
                e.preventDefault();
                return;
            }
            if (e.key === "ArrowUp" || e.key === "k") {
                setFocusedIndex((idx) => Math.max(0, idx - 1));
                e.preventDefault();
                return;
            }
            if (e.key === " ") {
                const p = (data?.fresh_providers || [])[focusedIndex];
                if (p) {
                    toggleSelect(p.id);
                    e.preventDefault();
                }
                return;
            }

            const num = Number(e.key);
            if (!Number.isNaN(num) && num >= 1 && num <= QUICK_ACTIONS.length) {
                const action = QUICK_ACTIONS[num - 1];
                const p = (data?.fresh_providers || [])[focusedIndex];
                if (p && action) {
                    // If selection is active, apply to selection; else apply to focused row
                    if (selected.size > 0) {
                        bulkUpdate(action.status);
                    } else {
                        updateStatus(p.id, action.status);
                    }
                    e.preventDefault();
                }
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [data, focusedIndex, selected, toggleSelect, updateStatus, bulkUpdate, clearSelection]);

    const summary = data?.summary || {};
    const bySearch = data?.by_search || [];
    const fresh = data?.fresh_providers || [];
    const followups = data?.followups || [];
    const warmLeads = data?.warm_leads || [];
    const noResponse = data?.no_response || [];

    const bySearchTop = useMemo(() => bySearch.slice(0, 5), [bySearch]);
    const selectedCount = selected.size;

    return (
        <div className="min-h-screen bg-white bg-grid">
            <main className="p-6 lg:p-8 max-w-[1600px] mx-auto w-full">
                <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <div className="label-kicker mb-2 flex items-center gap-2">
                            <Sun size={12} weight="bold" />
                            Daily Command Center
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                            Today's providers,
                            <br />
                            <span className="underline decoration-[3px]">ranked for outreach</span>.
                        </h1>
                        <p className="mt-3 text-[#52525b] text-sm max-w-xl">
                            Fresh, high-priority providers aggregated across every search your
                            team has run. Click an action — status and notes persist across future
                            runs.
                        </p>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                        <button
                            className="btn-secondary"
                            onClick={load}
                            disabled={loading}
                            data-testid="today-refresh"
                        >
                            <ArrowsClockwise size={14} weight="bold" />
                            {loading ? "Loading…" : "Refresh"}
                        </button>
                        <label className="flex items-center gap-2 text-[11px] text-[#52525b] cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={includeMedium}
                                onChange={(e) => setIncludeMedium(e.target.checked)}
                                data-testid="include-medium-toggle"
                            />
                            Include Medium priority (with contact)
                        </label>
                    </div>
                </div>

                {/* Summary tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
                    <Tile
                        Icon={Fire}
                        label="New today"
                        value={summary.new_today}
                        color="#002FA7"
                        testId="tile-new-today"
                    />
                    <Tile
                        Icon={Lightning}
                        label="High priority"
                        value={summary.high_priority}
                        color="#000"
                        testId="tile-high-priority"
                    />
                    <Tile
                        Icon={Clock}
                        label="Follow-ups due"
                        value={summary.followups_due}
                        color="#b07000"
                        testId="tile-followups-due"
                    />
                    <Tile
                        Icon={CheckCircle}
                        label="Interested (7d)"
                        value={summary.interested_this_week}
                        color="#002FA7"
                        testId="tile-interested"
                    />
                    <Tile
                        Icon={Trophy}
                        label="Claimed (7d)"
                        value={summary.claimed_this_week}
                        color="#000"
                        testId="tile-claimed"
                    />
                    <Tile
                        Icon={WarningCircle}
                        label="Invalid (7d)"
                        value={summary.invalid_this_week}
                        color="#ff3333"
                        testId="tile-invalid"
                    />
                    <Tile
                        Icon={PhoneCall}
                        label="Contacted (7d)"
                        value={summary.contacted_this_week}
                        color="#52525b"
                        testId="tile-contacted"
                    />
                </div>

                {/* By-search breakdown */}
                {bySearchTop.length > 0 && (
                    <div className="panel mb-6" data-testid="by-search-panel">
                        <div className="panel-header">
                            <div className="flex items-center gap-2">
                                <ChartLine size={14} weight="bold" />
                                <span className="label-kicker">Fresh providers by search</span>
                            </div>
                            <span className="mono text-xs text-[#52525b]">
                                {bySearch.length} active searches
                            </span>
                        </div>
                        <div className="p-4 flex flex-wrap gap-2">
                            {bySearchTop.map((s) => (
                                <span
                                    key={s.search_key}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#e4e4e7] mono text-[11px]"
                                    data-testid={`by-search-${s.search_key}`}
                                >
                                    <span className="font-bold">
                                        {s.category} · {s.location}
                                    </span>
                                    <span className="text-[#52525b]">
                                        +{s.new} new · {s.high} high · {s.count} total
                                    </span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Saved searches */}
                <div className="mb-6">
                    <SavedSearchesPanel onRunStarted={() => load()} />
                </div>

                {/* Bulk actions bar */}
                {selectedCount > 0 && (
                    <div
                        className="sticky top-16 z-20 bg-black text-white px-4 py-3 flex items-center justify-between gap-3 mb-4 flex-wrap"
                        data-testid="bulk-bar"
                        style={{ border: "1px solid #000" }}
                    >
                        <div className="flex items-center gap-3 mono text-xs">
                            <CheckSquare size={16} weight="bold" />
                            <span className="font-bold" data-testid="bulk-selected-count">
                                {selectedCount} selected
                            </span>
                            {operator ? (
                                <span className="opacity-70">as {operator}</span>
                            ) : (
                                <span className="opacity-70">
                                    (set an operator in top-right to attribute)
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-1 items-center">
                            {QUICK_ACTIONS.map(({ status, label, Icon }) => (
                                <button
                                    key={status}
                                    type="button"
                                    onClick={() => bulkUpdate(status)}
                                    className="inline-flex items-center gap-1 px-2 py-1 mono text-[10px] bg-white text-black hover:bg-[#ffc000] transition-colors"
                                    data-testid={`bulk-action-${status.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <Icon size={10} weight="bold" />
                                    {label}
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={clearSelection}
                                className="inline-flex items-center gap-1 px-2 py-1 mono text-[10px] border border-white hover:bg-[#ff3333] hover:border-[#ff3333] transition-colors ml-1"
                                data-testid="bulk-clear"
                            >
                                <X size={10} weight="bold" />
                                Clear
                            </button>
                        </div>
                    </div>
                )}

                {/* Follow-ups due */}
                {followups.length > 0 && (
                    <FollowupsSection
                        items={followups}
                        onStatus={updateStatus}
                        onNote={saveNote}
                        onClaim={handleClaim}
                        claimEnabled={!!data?.claim_url_base}
                    />
                )}

                {/* Warm leads + no response auto-surface */}
                {warmLeads.length > 0 && (
                    <AutoSurfaceSection
                        title="Warm Leads — Interested, not yet claimed"
                        tint="#002FA7"
                        items={warmLeads}
                        onStatus={updateStatus}
                        onNote={saveNote}
                        onClaim={handleClaim}
                        claimEnabled={!!data?.claim_url_base}
                        testId="warm-leads-section"
                    />
                )}

                {noResponse.length > 0 && (
                    <AutoSurfaceSection
                        title="No Response — followed-up but waiting"
                        tint="#b07000"
                        items={noResponse}
                        onStatus={updateStatus}
                        onNote={saveNote}
                        onClaim={handleClaim}
                        claimEnabled={!!data?.claim_url_base}
                        testId="no-response-section"
                    />
                )}

                {/* Fresh providers with selection */}
                <FreshProvidersSection
                    items={fresh}
                    loading={loading}
                    onStatus={updateStatus}
                    onNote={saveNote}
                    onClaim={handleClaim}
                    claimEnabled={!!data?.claim_url_base}
                    selected={selected}
                    onToggleSelect={toggleSelect}
                    onSelectAll={selectAllVisible}
                    onClearSelection={clearSelection}
                    focusedIndex={focusedIndex}
                    onFocusRow={setFocusedIndex}
                />

                <footer className="mt-10 pt-6 border-t border-[#e4e4e7] text-xs text-[#52525b] flex items-center justify-between flex-wrap gap-2">
                    <span>
                        Status changes persist across runs — this is your operational memory.
                    </span>
                    <span className="mono">
                        Today view · lookback {data?.lookback_days ?? 14}d · follow-up due ≥
                        {data?.followup_due_hours ?? 24}h
                    </span>
                </footer>
            </main>
        </div>
    );
}

function Tile({ Icon, label, value, color, testId }) {
    return (
        <div
            className="border border-[#e4e4e7] bg-white p-3 flex flex-col gap-1"
            data-testid={testId}
        >
            <span
                className="label-kicker flex items-center gap-1"
                style={{ fontSize: 9 }}
            >
                <Icon size={11} weight="bold" />
                {label}
            </span>
            <span className="font-display text-3xl font-bold" style={{ color }}>
                {value ?? 0}
            </span>
        </div>
    );
}

function FollowupsSection({ items, onStatus, onNote, onClaim, claimEnabled }) {
    return (
        <section className="panel mb-6" data-testid="followups-section">
            <div className="panel-header">
                <div className="flex items-center gap-2">
                    <Clock size={14} weight="bold" color="#b07000" />
                    <span className="label-kicker">Follow-ups Due</span>
                    <span className="mono text-[11px] text-[#52525b]">
                        {items.length} waiting
                    </span>
                </div>
            </div>
            <div className="divide-y divide-[#e4e4e7]">
                {items.map((p, i) => (
                    <ProviderRow
                        key={p.id}
                        provider={p}
                        index={i}
                        onStatus={onStatus}
                        onNote={onNote}
                        onClaim={onClaim}
                        claimEnabled={claimEnabled}
                        highlightFollowup
                    />
                ))}
            </div>
        </section>
    );
}

function FreshProvidersSection({
    items,
    loading,
    onStatus,
    onNote,
    onClaim,
    claimEnabled,
    selected,
    onToggleSelect,
    onSelectAll,
    onClearSelection,
    focusedIndex,
    onFocusRow,
}) {
    const allSelected =
        items.length > 0 && items.every((p) => selected && selected.has(p.id));
    return (
        <section className="panel" data-testid="fresh-providers-section">
            <div className="panel-header">
                <div className="flex items-center gap-2">
                    <Fire size={14} weight="bold" color="#002FA7" />
                    <span className="label-kicker">Fresh Providers</span>
                    <span className="mono text-[11px] text-[#52525b]">
                        {items.length} to action
                    </span>
                </div>
                {items.length > 0 && (
                    <div className="flex items-center gap-1">
                        <button
                            onClick={allSelected ? onClearSelection : onSelectAll}
                            className="btn-secondary"
                            style={{ padding: "4px 8px", fontSize: 11 }}
                            data-testid={allSelected ? "deselect-all" : "select-all"}
                        >
                            {allSelected ? (
                                <>
                                    <Square size={11} weight="bold" />
                                    Clear
                                </>
                            ) : (
                                <>
                                    <CheckSquare size={11} weight="bold" />
                                    Select all
                                </>
                            )}
                        </button>
                        <span className="mono text-[10px] text-[#52525b] ml-2">
                            ↑↓ to move · space to select · 1-9 to action
                        </span>
                    </div>
                )}
            </div>
            {loading && (
                <div className="p-10 text-center text-[#52525b] text-sm">
                    Loading…
                </div>
            )}
            {!loading && items.length === 0 && (
                <div
                    className="p-10 text-center text-[#52525b] text-sm"
                    data-testid="fresh-empty"
                >
                    No fresh providers to action right now. Run a search (Search tab) to
                    populate this list, or uncheck "Not Contacted only" filters if you've
                    actioned everything.
                </div>
            )}
            {!loading && items.length > 0 && (
                <div className="divide-y divide-[#e4e4e7]">
                    {items.map((p, i) => (
                        <ProviderRow
                            key={p.id}
                            provider={p}
                            index={i}
                            onStatus={onStatus}
                            onNote={onNote}
                            onClaim={onClaim}
                            claimEnabled={claimEnabled}
                            selected={selected?.has(p.id)}
                            onToggleSelect={onToggleSelect}
                            focused={focusedIndex === i}
                            onFocus={() => onFocusRow?.(i)}
                            showShortcuts
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

function AutoSurfaceSection({ title, tint, items, onStatus, onNote, onClaim, claimEnabled, testId }) {
    return (
        <section className="panel mb-6" data-testid={testId}>
            <div className="panel-header" style={{ borderLeft: `3px solid ${tint}` }}>
                <div className="flex items-center gap-2">
                    <span className="label-kicker" style={{ color: tint }}>{title}</span>
                    <span className="mono text-[11px] text-[#52525b]">
                        {items.length} waiting
                    </span>
                </div>
            </div>
            <div className="divide-y divide-[#e4e4e7]">
                {items.map((p, i) => (
                    <ProviderRow
                        key={p.id}
                        provider={p}
                        index={i}
                        onStatus={onStatus}
                        onNote={onNote}
                        onClaim={onClaim}
                        claimEnabled={claimEnabled}
                    />
                ))}
            </div>
        </section>
    );
}

function ProviderRow({
    provider,
    index,
    onStatus,
    onNote,
    onClaim,
    claimEnabled,
    highlightFollowup,
    selected,
    onToggleSelect,
    focused,
    onFocus,
    showShortcuts,
}) {
    const p = provider;
    const hostname = (() => {
        try {
            return p.website ? new URL(p.website).host.replace(/^www\./, "") : "";
        } catch {
            return "";
        }
    })();
    const hasCheckbox = typeof onToggleSelect === "function";
    return (
        <div
            onClick={onFocus}
            className={
                "p-4 grid grid-cols-1 lg:grid-cols-[32px_56px_minmax(220px,2fr)_minmax(240px,2fr)_minmax(260px,2.2fr)_minmax(200px,auto)] gap-4 items-start transition-colors " +
                (focused ? "bg-[#f4f4f5] " : "") +
                (highlightFollowup ? "bg-[#fff7e0] " : "") +
                (selected ? "outline outline-2 outline-black " : "")
            }
            data-testid={`fresh-row-${index}`}
        >
            <div className="flex items-start pt-1">
                {hasCheckbox ? (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleSelect(p.id);
                        }}
                        className="flex items-center justify-center"
                        data-testid={`row-select-${p.id}`}
                        aria-label={selected ? "Deselect" : "Select"}
                    >
                        {selected ? (
                            <CheckSquare size={16} weight="bold" />
                        ) : (
                            <Square size={16} weight="regular" color="#a1a1aa" />
                        )}
                    </button>
                ) : null}
            </div>
            <div className="flex flex-col items-start gap-1">
                <span className="mono text-[10px] text-[#a1a1aa]">
                    {String(index + 1).padStart(2, "0")}
                </span>
                <span
                    className="mono text-[10px] px-1.5 py-0.5 bg-black text-white"
                    data-testid={`today-score-${index}`}
                    title="Today priority score"
                >
                    {p.today_priority_score}
                </span>
            </div>
            <div className="flex flex-col gap-1 min-w-0">
                <div className="font-medium flex flex-wrap items-center gap-2">
                    {p.name || "—"}
                    {p.last_diff_status === "new" && (
                        <span className="mono text-[9px] px-1.5 py-0.5 bg-black text-white">
                            NEW
                        </span>
                    )}
                    {p.last_diff_status === "updated" && (
                        <span className="mono text-[9px] px-1.5 py-0.5 border border-[#b07000] text-[#b07000]">
                            UPDATED
                        </span>
                    )}
                    {p.priority_label === "High" && (
                        <span
                            className="mono text-[9px] px-1.5 py-0.5"
                            style={{ color: "#002FA7", border: "1px solid #002FA7" }}
                        >
                            HIGH
                        </span>
                    )}
                </div>
                <div className="label-kicker" style={{ fontSize: 9 }}>
                    {p.category} · {p.location}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                    {(p.today_reasons || []).map((r) => (
                        <span
                            key={r}
                            className="mono text-[9px] px-1.5 py-0.5 border border-[#e4e4e7] text-[#52525b]"
                        >
                            {r}
                        </span>
                    ))}
                </div>
            </div>
            <div className="flex flex-col gap-1 text-[12px] mono">
                {p.phone ? (
                    <a
                        href={`tel:${p.phone.replace(/[^+\d]/g, "")}`}
                        className="inline-flex items-center gap-1 hover:underline"
                    >
                        <PhoneCall size={11} />
                        {p.phone}
                    </a>
                ) : (
                    <span className="text-[#a1a1aa]">No phone</span>
                )}
                {p.email ? (
                    <a href={`mailto:${p.email}`} className="break-all hover:underline">
                        {p.email}
                    </a>
                ) : (
                    <span className="text-[#a1a1aa]">No email</span>
                )}
                {p.website && (
                    <a
                        href={p.website}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:underline text-[11px]"
                    >
                        {hostname}
                        <ArrowSquareOut size={10} />
                    </a>
                )}
            </div>
            <div className="flex flex-col gap-2 min-w-0">
                <QuickActions
                    currentStatus={p.contact_status}
                    onChange={(s) => onStatus(p.id, s)}
                    providerId={p.id}
                    showShortcuts={!!showShortcuts && !!focused}
                />
                <InlineNotes
                    value={p.contact_notes}
                    onSave={(n) => onNote(p.id, p.contact_status, n)}
                    providerId={p.id}
                />
            </div>
            <div className="flex flex-col gap-1 items-end">
                {claimEnabled && p.contact_status !== "Claimed Business" && (
                    <button
                        type="button"
                        onClick={() => onClaim(p)}
                        className="btn-primary"
                        style={{ padding: "6px 10px", fontSize: 11 }}
                        data-testid={`claim-btn-${p.id}`}
                    >
                        <Trophy size={12} weight="bold" />
                        Claim business
                    </button>
                )}
                {p.contact_status && p.contact_status !== "Not Contacted" && (
                    <span className="mono text-[10px] text-[#52525b] whitespace-nowrap">
                        Status: {p.contact_status}
                    </span>
                )}
                {p.hours_since_status != null && (
                    <span className="mono text-[10px] text-[#b07000] whitespace-nowrap">
                        {p.hours_since_status}h ago
                    </span>
                )}
                {p.days_since_status != null && (
                    <span className="mono text-[10px] text-[#b07000] whitespace-nowrap">
                        {p.days_since_status}d ago
                    </span>
                )}
            </div>
        </div>
    );
}
